import React from 'react'
import {Link} from 'gatsby'
import md5 from 'md5'
import {CognitoUserPool} from 'amazon-cognito-identity-js'
import {css} from '@emotion/core'

import CognitoStorage from '../../auth/cognito-storage'
import UserDropdown from '../user-dropdown'
import SlackLink from '../slack-link'
import logoGrad from '../../../img/8thWallLogo-Grad.png'
import logoGradWebp from '../../../img/8thWallLogo-Grad.webp'
import rocketIcon from '../../../img/rocketIcon.png'

const AWSConfig = {
  UserPoolId: process.env.USER_POOL_ID as string,
  ClientId: process.env.CLIENT_ID as string,
}

const cognitoStorage = new CognitoStorage()
const userPool = new CognitoUserPool({
  ...AWSConfig,
  Storage: cognitoStorage,
})
const signOutAndRemoveTokensFromStorage = (user) => {
  user.signOut()
  cognitoStorage.clear()
  window.location.reload()
}

export default () => {
  const [currentUser, setCurrentUser] = React.useState(null)
  const [userAttributes, setUserAttributes] = React.useState()
  const [showFreeTrial, setShowFreeTrial] = React.useState(true)
  const [isScrolled, setIsScrolled] = React.useState(false)

  React.useEffect(() => {
    setCurrentUser(userPool.getCurrentUser())
    const scrollListener = document.addEventListener('scroll', () => {
      if (window.scrollY >= 50) {
        if (!isScrolled) {
          setIsScrolled(true)
        }
      } else {
        setIsScrolled(false)
      }
    })
    return () => {
      document.removeEventListener('scroll', scrollListener)
    }
  }, [])

  React.useEffect(() => {
    if (currentUser) {
      currentUser.getSession((err, session) => {
        if (err) {
          console.error('There was an error getting the session for the current user', err)
          return
        }
        if (!session.isValid()) {
          console.log('not valid')
          return
        }

        currentUser.getUserAttributes((err, result) => {
          if (err) {
            console.error(err)
            return
          }

          // If user is not eligible for free trial, do not display [Start Free Trial] button.
          fetch('/v1/crm', {
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': session.getAccessToken().getJwtToken(),
            },
          })
            .then(res => res.json())
            .then((data) => {
              const ELIGIBLE_TRIAL_STATE = new Set(['NONE', 'ACCOUNT', 'BILLING'])
              if (!ELIGIBLE_TRIAL_STATE.has(data.free_trial_stage)) {
                setShowFreeTrial(false)
              }
            })
            .catch((error) => {
              console.error('There was an error fetching the trial status of the current user.', error)
            })

          const attributes = result.reduce((o, v) => {
            o[v.getName()] = v.getValue()
            return o
          }, {})
          attributes.uuid = currentUser.getUsername()
          attributes.confirmed = attributes.phone_number_verified === 'true'
          attributes.gravatarUrl = `https://www.gravatar.com/avatar/${md5(attributes.email.trim().toLowerCase())}?d=retro&r=g&s=36`
          setUserAttributes(attributes)

          // Collecting user data with Pendo
          if (window.pendo) {
            const params = {
              visitor: {
                id: attributes.uuid,
                email: attributes.email,
              },
              account: null,
            }
            if (typeof window.pendo.isReady === 'function' && window.pendo.isReady()) {
              window.pendo.identify(params)
            } else {
              window.pendo.initialize(params)
            }
          }

          // Enable Google Analytics User-ID feature using Cognito sub
          window.dataLayer.push({userId: currentUser.getUsername()})
        })
      })
    }
  }, [currentUser])

  return (
    <>
      <nav id='custom-nav' className={`navbar fixed-top navbar-expand-lg navbar-light bg-white ${isScrolled && 'shadow'}`} style={{zIndex: 11}}>
        <div className='col-5'>
          <a className={`${isScrolled && 'navbar-brand-shrink'} navbar-brand`} href='/' a8='click;navigation;go-to-home'>
            <picture>
              <source srcSet={logoGradWebp} type='image/webp' />
              <img className='logo float-left' src={logoGrad} alt='8th Wall Logo' />
            </picture>
          </a>
        </div>
        <button
          className='navbar-toggler'
          type='button'
          data-toggle='collapse'
          data-target='#navbarNav'
          aria-controls='navbarNav'
          aria-expanded='false'
          aria-label='Toggle navigation'
        >
          <span className='navbar-toggler-icon' />
        </button>

        <div className='collapse navbar-collapse' id='navbarNav'>
          <ul className='navbar-nav nav-fill w-100'>

            <li className='nav-item'>
              <Link className='nav-link' to='/products-web' a8='click;navigation;go-to-feature'>Product</Link>
            </li>

            <li className='nav-item'>
              <div
                className='dropdown'
                css={css`
                padding: 0.5rem;
                filter: drop-shadow(0 0 0.75rem #0003);
                `}
              >
                <a
                  className='drop-menu'
                  data-toggle='dropdown'
                  aria-haspopup='true'
                  aria-expanded='false'
                  href=''
                  a8='click;navigation;expand-customer-work'
                >
                  Customer Work&nbsp;<i className='fas fa-angle-down' css={css`font-size: 0.9rem;`} />
                </a>

                <div
                  className='dropdown-menu justify-content-center'
                  aria-labelledby='dropMenu'
                  css={css`
                  border-radius: 10px;
                  border: 0px;
                  top: 130%;
                  width: 100%;
                  `}
                >
                  <Link
                    className='dropdown-item'
                    to='customer-work'
                    a8='click;navigation;go-to-customer-work'
                  >
                    Inspiration
                  </Link>
                  <a
                    className='dropdown-item'
                    href='/partners'
                    a8='click;navigation;go-to-partners'
                  >
                    Partners
                  </a>
                </div>
              </div>
            </li>

            <li className='nav-item'>
              <Link className='nav-link' to='/pricing' a8='click;navigation;go-to-pricing'>Pricing</Link>
            </li>

            <li className='nav-item'>

              <div className='dropdown' style={{padding: '0.5rem', filter: 'drop-shadow(0 0 0.75rem #0003)'}}>
                <a className='drop-menu' data-toggle='dropdown' aria-haspopup='true' aria-expanded='false' href='' a8='click;navigation;expand-learn'>
                  Learn&nbsp;<i className='fas fa-angle-down' style={{fontSize: '0.9rem'}} />
                </a>

                <div
                  className='dropdown-menu justify-content-center'
                  aria-labelledby='dropMenu'
                  style={{borderRadius: '10px', border: '0px', top: '130%', width: '100%'}}
                >
                  <a className='dropdown-item' href='/8thwall' style={{color: '#ad50ff'}} a8='click;navigation;go-to-project-library'>
                    Project Library&nbsp;<img src={rocketIcon} style={{verticalAlign: 'middle', height: '1em'}} />
                  </a>
                  <a className='dropdown-item' href='/docs/web/' a8='click;navigation;go-to-docs'>Documentation</a>
                  <SlackLink className='dropdown-item' a8='click;navigation;join-community'>Slack Community</SlackLink>
                  <Link className='dropdown-item' to='resources' a8='click;navigation;go-to-support'>Support</Link>
                  <Link className='dropdown-item' to='tutorials' a8='click;navigation;go-to-tutorials'>Tutorials</Link>
                </div>

              </div>
            </li>

            <li className='nav-item'>
              <a className='nav-link' href='/blog' a8='click;navigation;go-to-blog'>Blog</a>
            </li>

            {showFreeTrial &&
              <>
                <li className='nav-item d-none d-lg-block'>
                  <a
                    href='/try-free-trial'
                    a8='click;navigation;go-to-free-trial'
                    id='createAcct'
                  >
                    Start free trial
                  </a>
                </li>
                <li className='nav-item d-lg-none'>
                  <a
                    href='/try-free-trial'
                    a8='click;navigation;go-to-free-trial'
                  >
                    <button
                      type='button'
                      className='btn btn-primary custom-btn'
                      css={css`
                      margin: 0.5rem auto;
                      padding-top: 0.25em;
                      padding-bottom: 0.25em;
                      `}
                    >
                      Start free trial
                    </button>
                  </a>
                </li>
              </>
            }

            {currentUser
              ? (
                <li className='nav-item'>
                  <UserDropdown user={userAttributes} signOut={() => signOutAndRemoveTokensFromStorage(currentUser)} />
                </li>
              )
              : (
                <li className='nav-item' id='loginParent'>
                  <a className='nav-link' href='/login' id='login' a8='click;navigation;login'>Log In</a>
                </li>
              )
            }
          </ul>
        </div>
      </nav>
    </>
  )
}
